import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReconnectingEventSource from 'reconnecting-eventsource';
import { useQueryClient } from '@tanstack/react-query';
import { GetGlobalSSEUrl } from '@/util/configureGlobalSSE';
import { getGameCamerasQueryKey } from '@/features/cameras/hooks/useGameCamerasQuery';
import { getQueryKey as getCamerasQueryKey } from '@/features/camera/hooks/useCamerasQuery';
import {
  GameScoresMap,
  GameScoreUpdate,
  RealtimeEventsContextValue,
  GameScoreEventPayload,
} from '../types/RealtimeEventTypes';

// Create context with default values
const RealtimeEventsContext = createContext<RealtimeEventsContextValue>({
  gameScores: {},
  isConnected: false,
  getGameScore: () => undefined,
});

interface RealtimeEventsProviderProps {
  children: ReactNode;
  enabled?: boolean; // Allow disabling the connection for testing
  token?: string; // Auth token for SSE connection
}

export const RealtimeEventsProvider = ({
  children,
  enabled = true,
  token,
}: RealtimeEventsProviderProps) => {
  const sseRef = useRef<ReconnectingEventSource | null>(null);
  const [gameScores, setGameScores] = useState<GameScoresMap>({});
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  // Process incoming SSE events
  const processRealtimeEvent = useCallback((data: any) => {
    const { event, payload } = data;

    switch (event) {
      case 'camera-status-update': {
        const gameId = payload?.gameId;
        console.debug('[RealtimeEvents] camera-status-update', { gameId, payload });
        if (!gameId) return;
        queryClient.invalidateQueries({ queryKey: getGameCamerasQueryKey(gameId) });
        queryClient.invalidateQueries({ queryKey: getCamerasQueryKey(gameId) });
        break;
      }

      case 'game-score-update': {
        const scoreData = payload as GameScoreEventPayload;

        if (!scoreData?.gameId) {
          console.warn('Received game-score-update without gameId:', scoreData);
          return;
        }

        // Update the game scores map
        setGameScores((prev) => ({
          ...prev,
          [scoreData.gameId]: {
            gameId: scoreData.gameId,
            homeScore: scoreData.homeScore,
            awayScore: scoreData.awayScore,
            period: scoreData.period,
            clock: scoreData.clock,
            time : scoreData.time,
            clockState: scoreData.clockState,
            timestamp: Date.now(),
          },
        }));
        break;
      }

      default:
        // Ignore unhandled event types (they may be handled by AuthContext)
        break;
    }
  }, [queryClient]);

  // Establish SSE connection
  useEffect(() => {
    // Only connect if enabled AND token is available
    if (!enabled || !token) {
      // Close existing connection if token becomes unavailable
      if (!token && sseRef.current) {
        console.debug('[RealtimeEvents] Token missing, closing SSE connection');
        sseRef.current.close();
        sseRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // Only create new connection if one doesn't exist
    if (!sseRef.current) {
      const sseUrl = `${GetGlobalSSEUrl()}?idToken=${token}`;

      try {
        console.debug('[RealtimeEvents] Establishing SSE connection with token');
        sseRef.current = new ReconnectingEventSource(sseUrl);

        sseRef.current.onopen = () => {
          console.debug('[RealtimeEvents] SSE connection established');
          setIsConnected(true);
        };

        sseRef.current.onerror = (error: any) => {
          console.debug('[RealtimeEvents] SSE connection error:', error);
          setIsConnected(false);

          // Check if it's a 403 Forbidden (auth failure)
          if (error?.status === 403 || error?.target?.status === 403) {
            console.error('[RealtimeEvents] SSE 403 Forbidden - closing connection to prevent retries');
            if (sseRef.current) {
              sseRef.current.close();
              sseRef.current = null;
            }
          }
        };

        sseRef.current.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            processRealtimeEvent(data);
          } catch (error) {
            console.error('[RealtimeEvents] Error processing SSE message:', error);
          }
        };
      } catch (error) {
        console.error('[RealtimeEvents] Error establishing SSE connection:', error);
        setIsConnected(false);
      }
    }

    // Cleanup SSE connection on unmount or when token becomes invalid
    return () => {
      if (sseRef.current) {
        console.debug('[RealtimeEvents] Cleaning up SSE connection');
        sseRef.current.close();
        sseRef.current = null;
        setIsConnected(false);
      }
    };
  }, [enabled, token, processRealtimeEvent]);

  // Helper to get a specific game's score
  const getGameScore = useCallback(
    (gameId: string): GameScoreUpdate | undefined => {
      return gameScores[gameId];
    },
    [gameScores]
  );

  const contextValue: RealtimeEventsContextValue = {
    gameScores,
    isConnected,
    getGameScore,
  };

  return (
    <RealtimeEventsContext.Provider value={contextValue}>
      {children}
    </RealtimeEventsContext.Provider>
  );
};

// Custom hook for using the realtime events context
export const useRealtimeEvents = () => useContext(RealtimeEventsContext);

export default RealtimeEventsContext;
